import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import "./EAModal.scss";
import { IoMdClose } from "react-icons/io";
import { IoChevronBack } from "react-icons/io5";
import EAIconButton from "../Buttons/EAIconButton";
import EAButton from "../Buttons/EAButton";
import { useTranslation } from "react-i18next";
import EASkeletonLoader from "../EASkeletonLoader";
import useEAModalIframeScrolling from "./useEAModalIframeScrolling";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import useDesktopSize from "../../../../utils/hooks/useDesktopSize";

const isEmbeddedFrame = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

const canConsumeScroll = (element: HTMLElement, deltaY: number) => {
  const overflowY = window.getComputedStyle(element).overflowY;
  if (overflowY !== "auto" && overflowY !== "scroll" && overflowY !== "overlay") return false;
  if (element.scrollHeight <= element.clientHeight + 1) return false;
  if (deltaY < 0) return element.scrollTop > 0;
  return element.scrollTop + element.clientHeight < element.scrollHeight - 1;
};

const scrollableAncestor = (target: EventTarget | null, boundary: HTMLElement, deltaY: number) => {
  let node = target instanceof HTMLElement ? target : null;
  while (node && boundary.contains(node)) {
    if (canConsumeScroll(node, deltaY)) return node;
    if (node === boundary) break;
    node = node.parentElement;
  }
  return null;
};

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  onAccept?: () => void;
  hideButtons?: boolean;
  cancelButtonLabel?: string;
  acceptButtonLabel?: string;
  hideAcceptButton?: boolean;
  loading?: boolean;
  isCloseButton?: boolean;
  desktopWidth?: string;
  onBack?: () => void;
  /** Marginesy od krawędzi viewportu (np. dla fixed nav). Na mobile domyślnie 80px góra/dół. */
  verticalMarginTop?: number;
  verticalMarginBottom?: number;
  disabledAccept?: boolean;
  /** Przypina footer z przyciskami na stałe do dołu modala — content scrolluje niezależnie. */
  stickyFooter?: boolean;
  /** Modal zajmuje dostępną wysokość. Przewija się tylko treść w środku, nie cała ramka. */
  fillAvailableHeight?: boolean;
}

const EAModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  title,
  children,
  onAccept,
  hideButtons = false,
  cancelButtonLabel = "",
  acceptButtonLabel = "",
  hideAcceptButton = false,
  loading = false,
  isCloseButton = true,
  desktopWidth,
  onBack,
  verticalMarginTop,
  verticalMarginBottom,
  disabledAccept = false,
  stickyFooter = false,
  fillAvailableHeight = false,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const isDesktop = useDesktopSize();
  const safeArea =
    verticalMarginTop !== undefined || verticalMarginBottom !== undefined
      ? {
          verticalMarginTop: verticalMarginTop ?? (isDesktop ? 5 : 80),
          verticalMarginBottom: verticalMarginBottom ?? (isDesktop ? 5 : 80),
        }
      : isDesktop
        ? undefined
        : { verticalMarginTop: 80, verticalMarginBottom: 80 };
  const { modalTop, maxHeight } = useEAModalIframeScrolling(modalRef, safeArea);
  const { updateScrollPosition } = useParentMessage();

  // Poza iframe blokujemy scroll dokumentu (html i body).
  // W iframe zostawiamy tylko body: pozycja modala dalej jedzie za scrollem strony rodzica.
  useEffect(() => {
    if (!isOpen) return;

    const embedded = isEmbeddedFrame();
    const html = document.documentElement;
    const { body } = document;
    const previousHtmlOverflow = html.style.overflow;
    const previousBodyOverflow = body.style.overflow;

    body.style.overflow = "hidden";
    if (!embedded) {
      html.style.overflow = "hidden";
    }

    requestAnimationFrame(() => {
      updateScrollPosition();
    });

    const overlay = modalRef.current?.parentElement;
    const onWheel = (event: WheelEvent) => {
      if (embedded || Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
      const container = modalRef.current;
      if (!container || !overlay?.contains(event.target as Node)) return;
      if (scrollableAncestor(event.target, container, event.deltaY)) return;

      event.preventDefault();
      const list = container.querySelector<HTMLElement>(".pass-selection__list");
      if (list && canConsumeScroll(list, event.deltaY)) {
        list.scrollTop += event.deltaY;
      }
    };

    overlay?.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      overlay?.removeEventListener("wheel", onWheel);
      html.style.overflow = previousHtmlOverflow;
      body.style.overflow = previousBodyOverflow;
    };
    //eslint-disable-next-line
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="eamodal-overlay"
      onClick={(e) => {
        if ((e.target as HTMLElement).classList.contains("eamodal-overlay")) {
          onClose();
        }
      }}
    >
      <div
        className={`eamodal-container${stickyFooter && !hideButtons ? " eamodal-container--sticky-footer" : ""}${
          fillAvailableHeight ? " eamodal-container--fill" : ""
        }`}
        ref={modalRef}
        style={{
          position: "absolute",
          top: modalTop,
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: maxHeight,
          ...(fillAvailableHeight ? { height: maxHeight } : {}),
          ...(desktopWidth && isDesktop ? { width: desktopWidth } : {}),
        }}
      >
        <div className="eamodal-header">
          {onBack && <EAIconButton title="Cofnij" icon={<IoChevronBack />} onClick={onBack} variant="icon" />}
          <h5 style={onBack ? { marginLeft: "8px" } : {}}>{title}</h5>
          {isCloseButton && <EAIconButton title="Zamknij" icon={<IoMdClose />} onClick={onClose} variant="icon" />}
        </div>
        {loading ? (
          <EASkeletonLoader width="100%" height="200px" />
        ) : (
          <>
            <div
              className={`eamodal-body${stickyFooter && !hideButtons ? " eamodal-body--with-sticky-footer" : ""}${
                fillAvailableHeight ? " eamodal-body--fill" : ""
              }`}
            >
              {children}
            </div>
            {!hideButtons && (
              <div className={`eamodal-footer${stickyFooter ? " eamodal-footer--sticky" : ""}`}>
                <EAButton variant="outlined" title={cancelButtonLabel || t("anuluj")} onClick={onClose} />
                {!hideAcceptButton && <EAButton title={acceptButtonLabel || t("akceptuj")} onClick={onAccept} disabled={disabledAccept} />}
              </div>
            )}
          </>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default EAModal;
